import type { Lang } from "@/lib/i18n";
import type { SubPageContent } from "@/components/common/SubPageLayout";

export const COPY: Record<Lang, SubPageContent> = {
  en: {
    breadcrumbKey: "SlitCoilsAndStampedFilterDiscs",
    relatedTopic: "components",
    hero: {
      categoryBadge: "Filter Components",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "Slit Coils and Stamped Filter Discs",
      lead: "Precision slit wire mesh coils and custom-stamped circular, annular, and shaped filter discs engineered for polymer processing, hydraulic filtration, chemical processing, and OEM liquid straining systems.",
      secondaryCta: {
        label: "Filter Components Overview",
        to: "/FilterComponentsPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stamped-stain-2c6f88.png",
        alt: "Precision stainless steel stamped filter discs and slit wire mesh coils",
      },
      cardNote: "Compound die stamping & slit coils",
      cardLink: {
        label: "View all components",
        to: "/FilterComponentsPage",
      },
      specs: [
        { label: "Diameter Range", value: "5 mm – 1,200 mm" },
        { label: "Micron Retention", value: "2 µm – 2,000 µm" },
        { label: "Thickness Range", value: "0.02 mm – 2.5 mm" },
        { label: "Standard Alloys", value: "AISI 304, 316, 316L" },
      ],
    },
    overview: {
      eyebrow: "OEM Manufacturing Precision",
      heading: "High-Tolerance Stamped Discs & Continuous Slit Mesh Coils",
      paragraphs: [
        "MutualFilter manufactures custom-dimensioned filter discs and narrow slit coils from premium stainless steel woven wire mesh. Using precision hardened tooling, compound dies, and dedicated slitting lines, each component is manufactured to strict geometric tolerances to ensure a dependable fit in process equipment.",
        "Stamped discs are supplied as single-layer discs, multilayer bonded disc packs, or rimmed variants edged with aluminum, copper, or stainless steel profiles. The edge construction seals mesh wires securely, preventing stray wire release into high-purity fluid streams.",
        "Continuous slit coils provide consistent mesh alignment and uniform edge trimming across long production runs, allowing seamless feeding into automated stamping equipment, plastic extrusion screen changers, or winding operations.",
      ],
      keyPoints: [
        "Precision compound die stamping with dimensional tolerances down to ±0.05 mm for tight housing clearances",
        "Continuous coil slitting from 5 mm width with clean, burr-minimized ultrasonic, laser, or shear-slit edges",
        "Single or multi-layer configurations with spot-welded, aluminum, copper, or stainless steel wrapped rims",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stamped-stain-2c6f88.png",
        alt: "Stainless steel stamped mesh discs with sealed rims and slit rolls",
      },
    },
    specTable: {
      heading: "Standard Dimensional & Technical Specifications",
      intro: "Representative manufacturing parameters for stamped mesh discs and slit narrow coils. Custom outer diameters, inner apertures, and layer combinations are fabricated to specification.",
      columns: [
        { key: "configuration", label: "Component Type", align: "left" },
        { key: "dimensions", label: "Dimensional Range", align: "right" },
        { key: "meshRange", label: "Mesh / Retention Range", align: "right" },
        { key: "edgeTreatment", label: "Edge Finishing", align: "left" },
        { key: "alloys", label: "Standard Materials", align: "left" },
      ],
      rows: [
        {
          id: "row-1",
          configuration: "Single-Layer Stamped Discs",
          dimensions: "Ø 5 mm – 1,200 mm",
          meshRange: "1–635 mesh (2 µm – 2,000 µm)",
          edgeTreatment: "Bare stamped / Ultrasonic cut",
          alloys: "AISI 304, 316, 316L",
        },
        {
          id: "row-2",
          configuration: "Spot-Welded Multilayer Discs",
          dimensions: "Ø 15 mm – 800 mm",
          meshRange: "2 µm – 500 µm combination",
          edgeTreatment: "Perimeter resistance spot welds",
          alloys: "AISI 304, 316, 316L",
        },
        {
          id: "row-3",
          configuration: "Framed / Rimmed Filter Discs",
          dimensions: "Ø 20 mm – 600 mm",
          meshRange: "5 µm – 1,000 µm combination",
          edgeTreatment: "Aluminum, copper, or SS rim",
          alloys: "AISI 304, 316, 316L",
        },
        {
          id: "row-4",
          configuration: "Continuous Slit Mesh Coils",
          dimensions: "Width: 5 mm – 1,000 mm",
          meshRange: "10–500 mesh",
          edgeTreatment: "Shear-slit / Ultrasonic stabilized",
          alloys: "AISI 304, 316, 316L",
        },
        {
          id: "row-5",
          configuration: "Annular & Shaped Screen Packs",
          dimensions: "Custom tooling to CAD",
          meshRange: "2 µm – 800 µm",
          edgeTreatment: "Crimped rim or spot-welded",
          alloys: "AISI 304, 316, 316L",
        },
      ],
      caption: "Material test reports and PMI inspection are available when specified in the order. Standard alloys include AISI 304, 316, and 316L.",
    },
    features: {
      heading: "Key Manufacturing Capabilities & Engineering Highlights",
      intro: "Our dedicated tooling, slitting, and rimming processes deliver structural integrity, uniform pore distribution, and dependable integration into OEM assemblies.",
      items: [
        {
          title: "Tight Geometric Tolerances",
          text: "Hardened steel tool sets and compound stamping dies achieve edge concentricity and diameter tolerances down to ±0.05 mm, preventing bypass in precision filter housings.",
        },
        {
          title: "Burr-Free Edge Stabilization",
          text: "Slit coils and stamped components undergo controlled shearing, ultrasonic, or mechanical edge trimming to prevent loose wire shed in critical hydraulic and polymer melt systems.",
        },
        {
          title: "Engineered Layer Stacking",
          text: "Combine coarse support wire cloth with fine Dutch or square weave filtration layers in structured packs to increase mechanical rigidity and extend on-stream service cycles.",
        },
        {
          title: "Custom Rim Encapsulation",
          text: "Perimeter rims fabricated from ductile aluminum, brass, copper, or deep-drawn stainless steel form a reliable gasket seal while locking multi-mesh edges permanently.",
        },
      ],
    },
    related: {
      heading: "Related Filter Components & Fabrications",
      intro: "Explore complementary wire mesh filter components and fabricated assemblies engineered for industrial straining and separation.",
      cards: [
        {
          title: "Filter Components Overview",
          text: "Comprehensive range of industrial filter elements, custom components, and precision wire mesh fabrications for OEM systems.",
          to: "/FilterComponentsPage",
          badge: "Parent Category",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-616af2.png",
            alt: "Fabricated industrial filter components overview",
          },
          linkLabel: "View components overview",
        },
        {
          title: "Filter Tubes & Cylinders",
          text: "Welded, rolled, and pleated stainless steel wire mesh cylinders and tubes for high-differential-pressure fluid lines.",
          to: "/FilterTubesAndCylindersPage",
          badge: "Cylindrical",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-616af2.png",
            alt: "Stainless steel filter tubes and cylinders",
          },
          linkLabel: "Explore filter tubes",
        },
        {
          title: "Filter Cartridges",
          text: "Pleated, cylindrical, and multi-layer stainless steel filter cartridges designed for reusable, cleanable liquid and gas filtration.",
          to: "/FilterCartridgesPage",
          badge: "Cartridges",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/Filter-Cartridges.jpg",
            alt: "Stainless steel pleated and cylindrical filter cartridges",
          },
          linkLabel: "Explore filter cartridges",
        },
        {
          title: "Screen Packs",
          text: "Assembled multi-layer wire mesh screen packs with spot-welded edges or crimped rims for polymer extrusion and blow molding.",
          to: "/ScreenPacksPage",
          badge: "Extrusion",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-7f27c4.png",
            alt: "Multi-layer wire mesh screen packs for extruders",
          },
          linkLabel: "Explore screen packs",
        },
        {
          title: "Filter Baskets & Plates",
          text: "Heavy-duty wire mesh filter baskets, perforated strainers, and support plates for batch filtration and industrial bag housings.",
          to: "/FilterBasketsAndPlatesPage",
          badge: "Straining",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-sta-d6091c.png",
            alt: "Industrial stainless steel filter baskets and strainer plates",
          },
          linkLabel: "Explore filter baskets",
        },
      ],
    },
    cta: {
      id: "slit-coils-stamped-discs-cta",
      eyebrow: "OEM Custom Filter Discs & Slit Mesh",
      heading: "Request Custom Disc Dimensions or Slit Coil Quotation",
      lead: "Provide your required outside diameter, retention rating, layer sequence, or drawing for a technical review and production pricing.",
      secondaryLabel: "Filter Components Hub",
      secondaryTo: "/FilterComponentsPage",
      note: "Custom tooling, spot-welding, and rim packaging engineered to order.",
    },
  },
  zh: {
    breadcrumbKey: "SlitCoilsAndStampedFilterDiscs",
    relatedTopic: "components",
    hero: {
      categoryBadge: "过滤组件",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "精密分条网带与冲压滤圆片",
      lead: "提供高精度分条金属丝网卷材，以及冲压圆形、环形和异形滤网片，广泛应用于聚合物加工、液压系统、化工流体及工业液体过滤设备。",
      secondaryCta: {
        label: "过滤组件概览",
        to: "/FilterComponentsPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stamped-stain-2c6f88.png",
        alt: "精密不锈钢冲压滤圆片与分条不锈钢丝网卷",
      },
      cardNote: "复合模冲压与窄幅精密分条",
      cardLink: {
        label: "查看所有组件",
        to: "/FilterComponentsPage",
      },
      specs: [
        { label: "外径范围", value: "5 mm – 1,200 mm" },
        { label: "过滤精度", value: "2 µm – 2,000 µm" },
        { label: "厚度规格", value: "0.02 mm – 2.5 mm" },
        { label: "标准合金", value: "AISI 304, 316, 316L" },
      ],
    },
    overview: {
      eyebrow: "OEM 精密制造工艺",
      heading: "严苛公差冲压滤片与连续分条金属网卷",
      paragraphs: [
        "互拓丝网（MutualFilter）依托成熟的不锈钢编织网制造经验，提供按图定制的滤网圆片及窄幅连续分条网卷。通过淬硬精密模具、复合落料模及专用分切生产线，确保每批产品具备严格的几何外形公差与装配同心度。",
        "冲压滤网片可提供单层结构、多层点焊复合结构，以及配有铝合金、紫铜或不锈钢包边圈的封边滤片。包边与封边工艺牢固包覆外缘丝头，杜绝金属丝在流体冲刷下松散脱落。",
        "连续分条网卷在长距离走网过程中保持平直且张力均匀，边缘平整无严重毛刺，可顺畅适配自动化冲压机、挤出机连续换网器以及缠绕加工工序。",
      ],
      keyPoints: [
        "精密复合模具冲压成型，外径公差可达 ±0.05 mm，确保与滤筒密封壳体紧密吻合",
        "连续网卷分切宽度自 5 mm 起，采用超声波、激光或机械剪切保证边缘齐整稳固",
        "单层及多层组合规格，支持多点电焊、铝框包边、铜圈包边及不锈钢封边加工",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stamped-stain-2c6f88.png",
        alt: "带包边不锈钢冲压滤网片与分条网带",
      },
    },
    specTable: {
      heading: "标准加工规格与几何尺寸参考",
      intro: "展示常规冲压滤网片与分条网带的加工范围。支持按工程图纸定制非标外径、内孔、多层阶梯组合及特殊包边形式。",
      columns: [
        { key: "configuration", label: "构件类型", align: "left" },
        { key: "dimensions", label: "尺寸范围", align: "right" },
        { key: "meshRange", label: "目数与精度范围", align: "right" },
        { key: "edgeTreatment", label: "边缘处理工艺", align: "left" },
        { key: "alloys", label: "标准材质", align: "left" },
      ],
      rows: [
        {
          id: "row-1",
          configuration: "单层冲压滤圆片",
          dimensions: "Ø 5 mm – 1,200 mm",
          meshRange: "1–635目 (2 µm – 2,000 µm)",
          edgeTreatment: "裸边模切 / 超声波切边",
          alloys: "AISI 304, 316, 316L",
        },
        {
          id: "row-2",
          configuration: "多层点焊滤网片",
          dimensions: "Ø 15 mm – 800 mm",
          meshRange: "2 µm – 500 µm 复合配比",
          edgeTreatment: "周边电阻多点点焊",
          alloys: "AISI 304, 316, 316L",
        },
        {
          id: "row-3",
          configuration: "金属包边密封滤盘",
          dimensions: "Ø 20 mm – 600 mm",
          meshRange: "5 µm – 1,000 µm 组合",
          edgeTreatment: "铝框、紫铜或不锈钢压制包边",
          alloys: "AISI 304, 316, 316L",
        },
        {
          id: "row-4",
          configuration: "连续窄幅分条网卷",
          dimensions: "宽度: 5 mm – 1,000 mm",
          meshRange: "10–500目",
          edgeTreatment: "精密圆刀剪切 / 超声波定边",
          alloys: "AISI 304, 316, 316L",
        },
        {
          id: "row-5",
          configuration: "环形垫片及异形过滤网",
          dimensions: "依 CAD 图纸开模定制",
          meshRange: "2 µm – 800 µm",
          edgeTreatment: "冷冲压成型 / 点焊固边",
          alloys: "AISI 304, 316, 316L",
        },
      ],
      caption: "材质报告 (MTR) 及 PMI 材质复验可在订单要求时提供。标准供应材质为 AISI 304、316 与 316L。",
    },
    features: {
      heading: "核心制造能力与工程技术特点",
      intro: "依靠成套模具制造能力、专用分条设备和规范的后处理流程，保障组件尺寸稳定且与工装严密匹配。",
      items: [
        {
          title: "严格的几何尺寸公差",
          text: "采用精密淬火模具与复合冲裁工艺，外径公差严控在 ±0.05 mm 以内，避免在配合法兰和滤腔内出现间隙旁漏。",
        },
        {
          title: "平整边缘与低毛刺控制",
          text: "分条网带与冲压边缘经过精密平整与去毛刺检验，降低高压液压与高粘度熔体过滤时的掉丝隐患。",
        },
        {
          title: "科学的多层网层阶梯配比",
          text: "支持粗目支撑网与细目过滤网（平织、斜织或密纹网）科学叠合，增强滤饼承托刚性并降低压降负荷。",
        },
        {
          title: "稳固的包边锁紧技术",
          text: "采用延展性良好的铝皮、红铜带或不锈钢板经模具卷压贴合，既能起到密封垫片效果，又牢固锁死各层网丝。",
        },
      ],
    },
    related: {
      heading: "相关过滤组件与深加工产品",
      intro: "浏览互拓更多工业丝网深加工滤元与成型部件，满足不同工业介质的拦截与分离需求。",
      cards: [
        {
          title: "过滤组件概览",
          text: "全面了解不锈钢丝网滤筒、滤盘、组合滤网及客制化成型过滤组件的技术参数与供货能力。",
          to: "/FilterComponentsPage",
          badge: "母级产品",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-616af2.png",
            alt: "不锈钢工业过滤组件与深加工构件概览",
          },
          linkLabel: "查看过滤组件概览",
        },
        {
          title: "滤筒与圆柱形滤管",
          text: "直缝焊接、卷圆及多层烧结丝网圆筒，提供高耐压抗冲击性能，适用于高压管路流体拦截。",
          to: "/FilterTubesAndCylindersPage",
          badge: "筒形组件",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-616af2.png",
            alt: "不锈钢丝网滤筒与圆管过滤构件",
          },
          linkLabel: "了解滤筒构件",
        },
        {
          title: "工业滤芯与滤芯组合件",
          text: "折叠式及圆筒式不锈钢高精度滤芯，结构坚固且支持多次反洗再生，用于液体与气体精细分离。",
          to: "/FilterCartridgesPage",
          badge: "滤芯",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/Filter-Cartridges.jpg",
            alt: "不锈钢折叠滤芯与圆筒滤芯",
          },
          linkLabel: "了解滤芯产品",
        },
        {
          title: "挤出机组合滤网",
          text: "用于塑料挤出造粒、吹膜及拉丝系统的多层点焊网片与铝包边换网包，承压性能好。",
          to: "/ScreenPacksPage",
          badge: "挤出换网",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-7f27c4.png",
            alt: "塑料挤出机专用多层不锈钢换网滤包",
          },
          linkLabel: "了解组合滤网",
        },
        {
          title: "过滤篮与滤板",
          text: "重型加强结构不锈钢滤篮、冲孔支撑篮与过滤隔板，适配各类袋式过滤器与管道过滤器外壳。",
          to: "/FilterBasketsAndPlatesPage",
          badge: "粗滤与蓝式",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-sta-d6091c.png",
            alt: "工业不锈钢过滤篮与滤板",
          },
          linkLabel: "了解过滤篮",
        },
      ],
    },
    cta: {
      id: "slit-coils-stamped-discs-cta",
      eyebrow: "OEM 定制滤网片与分条网带",
      heading: "获取定制滤片几何规格或批量分条网带报价",
      lead: "欢迎提供外径尺寸、过滤精度、叠层顺序或图纸，我们将为您提供技术核算与报价。",
      secondaryLabel: "过滤组件中心",
      secondaryTo: "/FilterComponentsPage",
      note: "支持非标开模、电焊多层组合及定制包边工艺。",
    },
  },
  es: {
    breadcrumbKey: "SlitCoilsAndStampedFilterDiscs",
    relatedTopic: "components",
    hero: {
      categoryBadge: "Componentes de filtración",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "Discos filtrantes estampados y bobinas cortadas",
      lead: "Discos filtrantes circulares, anulares y bobinas de malla metálica cortadas con alta precisión, fabricados para procesamiento de polímeros, sistemas hidráulicos y separación industrial.",
      secondaryCta: {
        label: "Resumen de componentes",
        to: "/FilterComponentsPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stamped-stain-2c6f88.png",
        alt: "Discos filtrantes estampados de acero inoxidable y bobinas cortadas",
      },
      cardNote: "Troquelado compuesto y corte longitudinal",
      cardLink: {
        label: "Ver todos los componentes",
        to: "/FilterComponentsPage",
      },
      specs: [
        { label: "Rango de diámetro", value: "5 mm – 1.200 mm" },
        { label: "Retención micrométrica", value: "2 µm – 2.000 µm" },
        { label: "Grosor", value: "0,02 mm – 2,5 mm" },
        { label: "Aleaciones estándar", value: "AISI 304, 316, 316L" },
      ],
    },
    overview: {
      eyebrow: "Fabricación OEM de precisión",
      heading: "Discos estampados de tolerancia estrecha y bobinas continuas",
      paragraphs: [
        "MutualFilter produce discos filtrantes a medida y bobinas de corte estrecho a partir de malla tejida de acero inoxidable de alta calidad. Mediante matrices templadas y líneas de corte específicas, cada elemento se fabrica con tolerancias geométricas rigurosas para un acoplamiento exacto.",
        "Los discos estampados se suministran en variantes monocapa, multicapa unidas por puntos o con bordes encapsulados en aluminio, cobre o acero inoxidable. Este cerramiento sella los alambres periféricos y evita desprendimientos en corrientes de fluido exigentes.",
        "Las bobinas cortadas de forma continua garantizan un ancho constante y bordes estables para alimentación automática en prensas, extrusoras o devanadoras.",
      ],
      keyPoints: [
        "Troquelado de precisión con matrices compuestas y tolerancias de hasta ±0,05 mm para ajuste perfecto",
        "Corte continuo desde 5 mm de ancho con bordes limpios mediante cizallado, láser o ultrasonido",
        "Configuraciones monocapa y multicapa con soldadura por puntos o marcos sellados de aluminio, cobre o acero",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stamped-stain-2c6f88.png",
        alt: "Discos de malla de acero inoxidable con marco y bobinas cortadas",
      },
    },
    specTable: {
      heading: "Especificaciones dimensionales y técnicas estándar",
      intro: "Parámetros representativos de discos estampados y bobinas de corte estrecho. Fabricación a medida según requisitos y planos CAD.",
      columns: [
        { key: "configuration", label: "Tipo de componente", align: "left" },
        { key: "dimensions", label: "Rango dimensional", align: "right" },
        { key: "meshRange", label: "Rango de malla / retención", align: "right" },
        { key: "edgeTreatment", label: "Acabado de bordes", align: "left" },
        { key: "alloys", label: "Aleaciones estándar", align: "left" },
      ],
      rows: [
        {
          id: "row-1",
          configuration: "Discos estampados monocapa",
          dimensions: "Ø 5 mm – 1.200 mm",
          meshRange: "1–635 mesh (2 µm – 2.000 µm)",
          edgeTreatment: "Borde cortado / Ultrasonido",
          alloys: "AISI 304, 316, 316L",
        },
        {
          id: "row-2",
          configuration: "Discos multicapa soldados por puntos",
          dimensions: "Ø 15 mm – 800 mm",
          meshRange: "Combinación 2 µm – 500 µm",
          edgeTreatment: "Soldadura perimetral por resistencia",
          alloys: "AISI 304, 316, 316L",
        },
        {
          id: "row-3",
          configuration: "Discos con marco metálico perimetral",
          dimensions: "Ø 20 mm – 600 mm",
          meshRange: "Combinación 5 µm – 1.000 µm",
          edgeTreatment: "Marco engastado de aluminio, cobre o inox",
          alloys: "AISI 304, 316, 316L",
        },
        {
          id: "row-4",
          configuration: "Bobinas cortadas continuas",
          dimensions: "Ancho: 5 mm – 1.000 mm",
          meshRange: "10–500 mesh",
          edgeTreatment: "Corte rotativo / Estabilizado ultrasónico",
          alloys: "AISI 304, 316, 316L",
        },
        {
          id: "row-5",
          configuration: "Packs anulares y geometrías especiales",
          dimensions: "Según plano o muestra",
          meshRange: "2 µm – 800 µm",
          edgeTreatment: "Borde engastado o soldado",
          alloys: "AISI 304, 316, 316L",
        },
      ],
      caption: "Certificados de material (MTR) y verificación PMI disponibles cuando se especifiquen en el pedido. Aleaciones estándar: AISI 304, 316 y 316L.",
    },
    features: {
      heading: "Capacidades de fabricación y puntos técnicos clave",
      intro: "Nuestros procesos de troquelado, corte y encapsulado proporcionan consistencia dimensional y alta resistencia estructural.",
      items: [
        {
          title: "Tolerancias dimensionales estrictas",
          text: "Matrices de acero templado que garantizan concentricidad y tolerancias de diámetro de hasta ±0,05 mm para evitar fugas.",
        },
        {
          title: "Bordes limpios sin desprendimiento",
          text: "Corte regulado mecánicamente o por ultrasonido para reducir rebabas y evitar la caída de alambres en circuitos críticos.",
        },
        {
          title: "Combinaciones multicapa estructuradas",
          text: "Unión de mallas finas de filtración con mallas gruesas de soporte para incrementar la rigidez mecánica.",
        },
        {
          title: "Encapsulado con marco perimetral",
          text: "Marcos en aluminio dúctil, cobre o acero inoxidable que sellan las capas y actúan como junta perimetral estanca.",
        },
      ],
    },
    related: {
      heading: "Componentes filtrantes relacionados",
      intro: "Conozca otros elementos de filtración y ensamblajes de malla fabricados para aplicaciones industriales.",
      cards: [
        {
          title: "Resumen de componentes filtrantes",
          text: "Gama completa de tubos, discos, cartuchos y cestas de malla de alambre de acero inoxidable.",
          to: "/FilterComponentsPage",
          badge: "Familia principal",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-616af2.png",
            alt: "Resumen de componentes filtrantes de acero inoxidable",
          },
          linkLabel: "Ver catálogo de componentes",
        },
        {
          title: "Tubos y cilindros filtrantes",
          text: "Cilindros de malla de alambre soldados longitudinalmente y conformados para líneas de flujo presurizadas.",
          to: "/FilterTubesAndCylindersPage",
          badge: "Cilíndricos",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-616af2.png",
            alt: "Tubos y cilindros filtrantes de acero inoxidable",
          },
          linkLabel: "Ver tubos filtrantes",
        },
        {
          title: "Cartuchos filtrantes",
          text: "Cartuchos plisados y cilíndricos lavables y reutilizables en acero inoxidable para líquidos y gases.",
          to: "/FilterCartridgesPage",
          badge: "Cartuchos",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/Filter-Cartridges.jpg",
            alt: "Cartuchos filtrantes de acero inoxidable",
          },
          linkLabel: "Ver cartuchos filtrantes",
        },
        {
          title: "Packs de mallas para extrusión",
          text: "Paquetes multicapa con bordes soldados o engastados en aluminio para extrusoras de plástico y polímeros.",
          to: "/ScreenPacksPage",
          badge: "Extrusión",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-7f27c4.png",
            alt: "Packs de mallas para extrusión",
          },
          linkLabel: "Ver packs de extrusión",
        },
        {
          title: "Cestas y placas filtrantes",
          text: "Cestas filtrantes reforzadas y placas perforadas de soporte para carcasas de bolsa y filtración en línea.",
          to: "/FilterBasketsAndPlatesPage",
          badge: "Cestas",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-sta-d6091c.png",
            alt: "Cestas filtrantes y placas de acero inoxidable",
          },
          linkLabel: "Ver cestas filtrantes",
        },
      ],
    },
    cta: {
      id: "slit-coils-stamped-discs-cta",
      eyebrow: "Discos filtrantes y bobinas a medida",
      heading: "Solicite cotización de discos estampados o bobinas cortadas",
      lead: "Indique diámetros requeridos, retención micrométrica, secuencia de capas o planos para estudio técnico.",
      secondaryLabel: "Centro de componentes",
      secondaryTo: "/FilterComponentsPage",
      note: "Matrices a medida, soldadura por puntos y encapsulado según especificación.",
    },
  },
  de: {
    breadcrumbKey: "SlitCoilsAndStampedFilterDiscs",
    relatedTopic: "components",
    hero: {
      categoryBadge: "Filterkomponenten",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "Präzisions-Filterronden & Spaltbänder",
      lead: "Präzise längsgeteilte Drahtgewebebänder sowie gestanzte runde, ringförmige und formatierte Filterscheiben für Polymerverarbeitung, Hydraulik und industrielle Flüssigkeitsfiltration.",
      secondaryCta: {
        label: "Übersicht Filterkomponenten",
        to: "/FilterComponentsPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stamped-stain-2c6f88.png",
        alt: "Präzisionsgestanzte Edelstahl-Filterronden und Drahtgewebe-Spaltbänder",
      },
      cardNote: "Verbundwerkzeug-Stanzen & Schmalspaltbänder",
      cardLink: {
        label: "Alle Komponenten anzeigen",
        to: "/FilterComponentsPage",
      },
      specs: [
        { label: "Durchmesserbereich", value: "5 mm – 1.200 mm" },
        { label: "Filterfeinheit", value: "2 µm – 2.000 µm" },
        { label: "Dickenbereich", value: "0,02 mm – 2,5 mm" },
        { label: "Standardlegierungen", value: "AISI 304, 316, 316L" },
      ],
    },
    overview: {
      eyebrow: "OEM-Präzisionsfertigung",
      heading: "Maßhaltige Stanzronden & kontinuierlich gespaltene Gewebebänder",
      paragraphs: [
        "MutualFilter fertigt maßgeschneiderte Filterronden und schmale Spaltbänder aus hochwertigem Edelstahldrahtgewebe. Mit gehärteten Stanzwerkzeugen, Verbundwerkzeugen und modernen Längsteilanlagen entstehen Komponenten mit engsten geometrischen Toleranzen.",
        "Die Filterscheiben werden als Einzellagen, mehrlagig punktgeschweißte Verbunde oder mit gefassten Kanten aus Aluminium, Kupfer oder Edelstahl geliefert. Die Kantenfassung sichert Randdrähte zuverlässig gegen Ausfasern im Fluidstrom.",
        "Kontinuierlich geteilte Drahtgewebe-Spaltbänder gewährleisten gleichmäßige Bandbreiten und saubere Schnittkanten für automatische Zuführungen, Siebwechsler oder Wickelanwendungen.",
      ],
      keyPoints: [
        "Präzisionsstanzen mit Werkzeugtoleranzen bis ±0,05 mm für passgenauen Einbau in Gehäuse und Dichtsitze",
        "Kontinuierliches Längsteilen ab 5 mm Breite mit gratarmen, ultraschall- oder lasergeschnittenen Kanten",
        "Ein- und mehrlagige Konfigurationen mit Punktschweißung oder Kantenfassung aus Aluminium, Kupfer und Edelstahl",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stamped-stain-2c6f88.png",
        alt: "Gefasste Filterronden und Drahtgewebespaltbänder aus Edelstahl",
      },
    },
    specTable: {
      heading: "Typische Abmessungen & technische Fertigungsparameter",
      intro: "Fertigungsbereiche für gestanzte Ronden und Schmalbänder. Kundenspezifische Durchmesser, Lochmaße und Lagenschichtungen werden auftragsbezogen hergestellt.",
      columns: [
        { key: "configuration", label: "Komponententyp", align: "left" },
        { key: "dimensions", label: "Abmessungsbereich", align: "right" },
        { key: "meshRange", label: "Maschenweite / Feinheit", align: "right" },
        { key: "edgeTreatment", label: "Kantenausführung", align: "left" },
        { key: "alloys", label: "Standardwerkstoffe", align: "left" },
      ],
      rows: [
        {
          id: "row-1",
          configuration: "Einlagige Stanzronden",
          dimensions: "Ø 5 mm – 1.200 mm",
          meshRange: "1–635 Mesh (2 µm – 2.000 µm)",
          edgeTreatment: "Glatt gestanzt / Ultraschallschnitt",
          alloys: "AISI 304, 316, 316L",
        },
        {
          id: "row-2",
          configuration: "Mehrlagige punktgeschweißte Ronden",
          dimensions: "Ø 15 mm – 800 mm",
          meshRange: "2 µm – 500 µm Verbund",
          edgeTreatment: "Umfang-Widerstandspunktschweißung",
          alloys: "AISI 304, 316, 316L",
        },
        {
          id: "row-3",
          configuration: "Gefasste Filterronden",
          dimensions: "Ø 20 mm – 600 mm",
          meshRange: "5 µm – 1.000 µm Verbund",
          edgeTreatment: "Alu-, Kupfer- oder Edelstahlfassung",
          alloys: "AISI 304, 316, 316L",
        },
        {
          id: "row-4",
          configuration: "Kontinuierliche Spaltbänder",
          dimensions: "Breite: 5 mm – 1.000 mm",
          meshRange: "10–500 Mesh",
          edgeTreatment: "Rollenscherenschnitt / Ultraschallfixiert",
          alloys: "AISI 304, 316, 316L",
        },
        {
          id: "row-5",
          configuration: "Ring- und Formfilterpakete",
          dimensions: "Nach Zeichnungsvorgabe",
          meshRange: "2 µm – 800 µm",
          edgeTreatment: "Gefasst oder punktgeschweißt",
          alloys: "AISI 304, 316, 316L",
        },
      ],
      caption: "Werkszeugnisse (MTR) und PMI-Prüfung auf Anfrage verfügbar, wenn in der Bestellung spezifiziert. Standardwerkstoffe: AISI 304, 316 und 316L.",
    },
    features: {
      heading: "Fertigungskompetenzen & Konstruktionsvorteile",
      intro: "Präzise Stanz- und Spaltverfahren garantieren hohe Wiederholgenauigkeit und mechanische Stabilität.",
      items: [
        {
          title: "Geringe Geometrietoleranzen",
          text: "Werkzeuge aus gehärtetem Werkzeugstahl sichern Durchmesser- und Rundheitstoleranzen bis ±0,05 mm ohne Spaltbildung.",
        },
        {
          title: "Saubere, ausfasersichere Kanten",
          text: "Gezielte mechanische oder ultraschallbasierte Schnittführung verhindert Drahtbruch und Partikelaustrag in empfindliche Kreisläufe.",
        },
        {
          title: "Mehrlagiger Schichtenaufbau",
          text: "Kombination aus feinen Filterlagen und stabilisierenden Stützgeweben zur Optimierung von Druckfestigkeit und Standzeit.",
        },
        {
          title: "Feste Kantenfassung",
          text: "Umlaufende Fassungen aus Aluminium, Kupfer oder Edelstahl bieten zusätzliche Dichtfunktion und schließen alle Ränder fest ein.",
        },
      ],
    },
    related: {
      heading: "Verwandte Filterkomponenten & Baugruppen",
      intro: "Entdecken Sie ergänzende Edelstahlgewebe-Baugruppen für anspruchsvolle Trenn- und Filtrationsanwendungen.",
      cards: [
        {
          title: "Übersicht Filterkomponenten",
          text: "Gesamtspektrum konfektionierter Filterelemente, Ronden, Zylinder und Körbe aus Edelstahldrahtgewebe.",
          to: "/FilterComponentsPage",
          badge: "Hauptkategorie",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-616af2.png",
            alt: "Übersicht gefertigter Edelstahl-Filterkomponenten",
          },
          linkLabel: "Komponentenübersicht ansehen",
        },
        {
          title: "Filterrohre & Zylinder",
          text: "Längsgeschweißte und gerollte Gewebezylinder für filtrationsintensive Prozessleitungen und Schutzrohre.",
          to: "/FilterTubesAndCylindersPage",
          badge: "Zylindrisch",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-616af2.png",
            alt: "Edelstahl-Filterrohre und Zylinder",
          },
          linkLabel: "Filterrohre erkunden",
        },
        {
          title: "Filterkerzen & Patronen",
          text: "Plissierte und zylindrische Edelstahl-Filterkerzen für reinigbare und langlebige Fest-Flüssig-Trennung.",
          to: "/FilterCartridgesPage",
          badge: "Filterkerzen",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/Filter-Cartridges.jpg",
            alt: "Plissierte und glatte Edelstahl-Filterkerzen",
          },
          linkLabel: "Filterkerzen erkunden",
        },
        {
          title: "Siebpakete für Extrusion",
          text: "Mehrlagige Drahtgewebepakete mit Punktschweißung oder Alufassung für Polymer- und Kunststoffextruder.",
          to: "/ScreenPacksPage",
          badge: "Extrusion",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-7f27c4.png",
            alt: "Mehrlagige Siebpakete für Extrusionsanlagen",
          },
          linkLabel: "Siebpakete erkunden",
        },
        {
          title: "Filterkörbe & Siebböden",
          text: "Robuste Edelstahl-Filterkörbe und Lochblech-Stützkörbe für Beutelfiltergehäuse und Rohrleitungsfilter.",
          to: "/FilterBasketsAndPlatesPage",
          badge: "Filterkörbe",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-sta-d6091c.png",
            alt: "Edelstahl-Filterkörbe und Siebplatten",
          },
          linkLabel: "Filterkörbe erkunden",
        },
      ],
    },
    cta: {
      id: "slit-coils-stamped-discs-cta",
      eyebrow: "OEM-Fertigung für Ronden & Spaltbänder",
      heading: "Individuelle Abmessungen oder Spaltband-Angebot anfordern",
      lead: "Senden Sie uns Außenmaße, Feinheiten, Lagenaufbauten oder Zeichnungen für ein technisches Angebot.",
      secondaryLabel: "Komponenten-Bereich",
      secondaryTo: "/FilterComponentsPage",
      note: "Werkzeugbau, Punktschweißung und Kantenfassungen nach Kundenspezifikation.",
    },
  },
  fr: {
    breadcrumbKey: "SlitCoilsAndStampedFilterDiscs",
    relatedTopic: "components",
    hero: {
      categoryBadge: "Composants filtrants",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "Disques filtrants découpés et bandes refendues",
      lead: "Disques circulaires, annulaires et bandes de toile métallique refendues avec précision pour le traitement des polymères, l'hydraulique et la filtration industrielle.",
      secondaryCta: {
        label: "Aperçu des composants",
        to: "/FilterComponentsPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stamped-stain-2c6f88.png",
        alt: "Disques filtrants découpés et bandes de toile métallique en acier inoxydable",
      },
      cardNote: "Découpage par outil combiné & refendage continu",
      cardLink: {
        label: "Voir tous les composants",
        to: "/FilterComponentsPage",
      },
      specs: [
        { label: "Plage de diamètres", value: "5 mm – 1 200 mm" },
        { label: "Seuil de filtration", value: "2 µm – 2 000 µm" },
        { label: "Épaisseur", value: "0,02 mm – 2,5 mm" },
        { label: "Alliages standards", value: "AISI 304, 316, 316L" },
      ],
    },
    overview: {
      eyebrow: "Fabrication OEM de haute précision",
      heading: "Disques découpés à tolérances strictes et bandes refendues",
      paragraphs: [
        "MutualFilter fabrique des disques filtrants sur mesure et des bandes étroites refendues en toile métallique d'acier inoxydable de première qualité. Grâce à des outillages trempés et des lignes de coupe dédiées, chaque pièce respecte des tolérances géométriques rigoureuses.",
        "Les disques découpés sont proposés en versions monocouche, multicouches assemblées par points ou avec bordure sertie en aluminium, cuivre ou inox. Cette bordure encapsule parfaitement les fils périphériques pour éviter tout effilochage.",
        "Les bobines refendues en continu assurent une largeur stable et une découpe sans bavure, idéales pour l'alimentation automatisée de presses, d'extrudeuses ou d'opérations d'enroulement.",
      ],
      keyPoints: [
        "Découpage à l'outil de précision avec tolérances jusqu'à ±0,05 mm pour un ajustement parfait dans les corps de filtre",
        "Refendage continu dès 5 mm de largeur avec bords nets traités par ultrasons, laser ou cisaillage",
        "Configurations mono ou multicouches soudées par points ou bordées d'aluminium, de cuivre et d'inox",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stamped-stain-2c6f88.png",
        alt: "Disques filtrants sertis et rouleaux de toile métallique refendue",
      },
    },
    specTable: {
      heading: "Spécifications dimensionnelles et paramètres techniques",
      intro: "Plages indicatives de fabrication pour disques découpés et bandes refendues. Dimensions spécifiques, ouvertures internes et empilements sur mesure selon plan.",
      columns: [
        { key: "configuration", label: "Type de composant", align: "left" },
        { key: "dimensions", label: "Plage dimensionnelle", align: "right" },
        { key: "meshRange", label: "Mesh / Seuil de filtration", align: "right" },
        { key: "edgeTreatment", label: "Finition des bords", align: "left" },
        { key: "alloys", label: "Nuances standard", align: "left" },
      ],
      rows: [
        {
          id: "row-1",
          configuration: "Disques découpés monocouche",
          dimensions: "Ø 5 mm – 1 200 mm",
          meshRange: "1–635 mesh (2 µm – 2 000 µm)",
          edgeTreatment: "Bord brut découpé / Coupe ultrasons",
          alloys: "AISI 304, 316, 316L",
        },
        {
          id: "row-2",
          configuration: "Disques multicouches soudés par points",
          dimensions: "Ø 15 mm – 800 mm",
          meshRange: "Combinaisons 2 µm – 500 µm",
          edgeTreatment: "Soudure par résistance périphérique",
          alloys: "AISI 304, 316, 316L",
        },
        {
          id: "row-3",
          configuration: "Disques filtrants avec bordure sertie",
          dimensions: "Ø 20 mm – 600 mm",
          meshRange: "Combinaisons 5 µm – 1 000 µm",
          edgeTreatment: "Bordure sertie alu, cuivre ou inox",
          alloys: "AISI 304, 316, 316L",
        },
        {
          id: "row-4",
          configuration: "Bandes refendues en continu",
          dimensions: "Largeur : 5 mm – 1 000 mm",
          meshRange: "10–500 mesh",
          edgeTreatment: "Cisaillage rotatif / Bords stabilisés",
          alloys: "AISI 304, 316, 316L",
        },
        {
          id: "row-5",
          configuration: "Packs annulaires et formes complexes",
          dimensions: "Selon dessin ou plan CAO",
          meshRange: "2 µm – 800 µm",
          edgeTreatment: "Bordure sertie ou soudure par points",
          alloys: "AISI 304, 316, 316L",
        },
      ],
      caption: "Certificats de matière (MTR) et contrôle PMI disponibles sur spécification à la commande. Nuances standards : AISI 304, 316 et 316L.",
    },
    features: {
      heading: "Atouts techniques et capacités de fabrication",
      intro: "Nos procédés d'outillage, de refendage et de sertissage garantissent une régularité dimensionnelle et une tenue mécanique optimale.",
      items: [
        {
          title: "Tolérances géométriques strictes",
          text: "Outillages en acier trempé assurant des tolérances jusqu'à ±0,05 mm pour éliminer les risques de dérivation de flux.",
        },
        {
          title: "Bords nets et sans bavures",
          text: "Découpe et cisaillage contrôlés pour minimiser les bavures et prévenir la chute de fils dans les circuits sensibles.",
        },
        {
          title: "Empilements multicouches sur mesure",
          text: "Association de toiles filtrantes fines et de toiles de renfort épaisses pour renforcer la rigidité sous pression.",
        },
        {
          title: "Sertissage périphérique protecteur",
          text: "Bordures en aluminium, cuivre ou inox assurant à la fois le verrouillage des toiles et l'étanchéité mécanique.",
        },
      ],
    },
    related: {
      heading: "Composants filtrants associés",
      intro: "Découvrez notre gamme complète d'éléments filtrants métalliques et d'assemblages mécano-soudés pour l'industrie.",
      cards: [
        {
          title: "Aperçu des composants filtrants",
          text: "Ensemble complet des cartouches, tubes, paniers et éléments spécifiques en toile métallique inox.",
          to: "/FilterComponentsPage",
          badge: "Catégorie mère",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-616af2.png",
            alt: "Aperçu des composants filtrants métalliques",
          },
          linkLabel: "Voir tous les composants",
        },
        {
          title: "Tubes et cylindres filtrants",
          text: "Cylindres en toile métallique roulés et soudés longitudinalement pour conduites de fluides sous pression.",
          to: "/FilterTubesAndCylindersPage",
          badge: "Cylindres",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-616af2.png",
            alt: "Tubes et cylindres filtrants en acier inoxydable",
          },
          linkLabel: "Découvrir les tubes filtrants",
        },
        {
          title: "Cartouches filtrantes",
          text: "Cartouches en inox plissées ou lisses lavables et réutilisables pour filtration fine de liquides et gaz.",
          to: "/FilterCartridgesPage",
          badge: "Cartouches",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/Filter-Cartridges.jpg",
            alt: "Cartouches filtrantes plissées en acier inoxydable",
          },
          linkLabel: "Découvrir les cartouches",
        },
        {
          title: "Packs de toiles pour extrusion",
          text: "Packs multicouches soudés par points ou sertis aluminium pour extrudeuses de polymères et plastique.",
          to: "/ScreenPacksPage",
          badge: "Extrusion",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-7f27c4.png",
            alt: "Packs de toiles pour extrusion plastique",
          },
          linkLabel: "Découvrir les packs d'extrusion",
        },
        {
          title: "Paniers et plateaux filtrants",
          text: "Paniers filtrants renforcés et plateaux perforés supportant les poches filtrantes et les filtres à tamis.",
          to: "/FilterBasketsAndPlatesPage",
          badge: "Paniers",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-sta-d6091c.png",
            alt: "Paniers et plateaux filtrants en acier inoxydable",
          },
          linkLabel: "Découvrir les paniers filtrants",
        },
      ],
    },
    cta: {
      id: "slit-coils-stamped-discs-cta",
      eyebrow: "Fabrication sur mesure de disques et bandes",
      heading: "Demandez une étude ou un devis pour vos disques et bandes",
      lead: "Précisez vos diamètres, seuils de coupure, séquences de couches ou plans pour une offre technique personnalisée.",
      secondaryLabel: "Pôle composants filtrants",
      secondaryTo: "/FilterComponentsPage",
      note: "Outillage sur mesure, soudure par points et sertissage selon cahier des charges.",
    },
  },
  pt: {
    breadcrumbKey: "SlitCoilsAndStampedFilterDiscs",
    relatedTopic: "components",
    hero: {
      categoryBadge: "Componentes de filtração",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "Discos filtrantes estampados e rolos cortados",
      lead: "Rolos de tela de arame cortados com precisão e discos filtrantes circulares, anulares e conformados para processamento de polímeros, sistemas hidráulicos e filtração industrial.",
      secondaryCta: {
        label: "Visão geral de componentes",
        to: "/FilterComponentsPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stamped-stain-2c6f88.png",
        alt: "Discos de filtro de aço inoxidável estampados e rolos de malha de arame cortados",
      },
      cardNote: "Estampagem com matrizes compostas e corte contínuo",
      cardLink: {
        label: "Ver todos os componentes",
        to: "/FilterComponentsPage",
      },
      specs: [
        { label: "Faixa de diâmetro", value: "5 mm – 1.200 mm" },
        { label: "Retenção micrométrica", value: "2 µm – 2.000 µm" },
        { label: "Espessura", value: "0,02 mm – 2,5 mm" },
        { label: "Ligas padrão", value: "AISI 304, 316, 316L" },
      ],
    },
    overview: {
      eyebrow: "Fabricação de precisão OEM",
      heading: "Discos estampados de alta tolerância e rolos contínuos cortados",
      paragraphs: [
        "A MutualFilter produz discos filtrantes sob medida e rolos de corte estreito a partir de telas tecidas em aço inoxidável de alta qualidade. Utilizando matrizes temperadas e linhas dedicadas de corte longitudinal, cada peça é fabricada dentro de rigorosas tolerâncias geométricas.",
        "Os discos estampados são fornecidos em modelos de camada única, pacotes multicamada soldados por pontos ou discos com aros de reforço em alumínio, cobre ou aço inox. O aro sela firmemente as bordas para evitar desprendimento de fios em fluxos de alta exigência.",
        "Os rolos cortados continuamente garantem largura uniforme e bordas regulares para alimentação automática em prensas de estampagem, trocadores de tela em extrusoras ou processos de enrolamento.",
      ],
      keyPoints: [
        "Estampagem com matrizes de precisão com tolerâncias de até ±0,05 mm para assentamento perfeito em carcaças",
        "Corte longitudinal contínuo a partir de 5 mm de largura com bordas limpas por cisalhamento, laser ou ultrassom",
        "Configurações de camada única e multicamada com solda a ponto ou bordas encapsuladas em alumínio, cobre ou inox",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stamped-stain-2c6f88.png",
        alt: "Discos de malha de aço inoxidável com aro e rolos cortados",
      },
    },
    specTable: {
      heading: "Especificações dimensionais e parâmetros técnicos",
      intro: "Parâmetros representativos para discos de tela estampados e rolos estreitos cortados. Medidas customizadas e pacotes especiais fabricados sob encomenda.",
      columns: [
        { key: "configuration", label: "Tipo de componente", align: "left" },
        { key: "dimensions", label: "Faixa dimensional", align: "right" },
        { key: "meshRange", label: "Malha / Retenção", align: "right" },
        { key: "edgeTreatment", label: "Acabamento de borda", align: "left" },
        { key: "alloys", label: "Ligas padrão", align: "left" },
      ],
      rows: [
        {
          id: "row-1",
          configuration: "Discos estampados de camada única",
          dimensions: "Ø 5 mm – 1.200 mm",
          meshRange: "1–635 mesh (2 µm – 2.000 µm)",
          edgeTreatment: "Borda cortada / Corte ultrassônico",
          alloys: "AISI 304, 316, 316L",
        },
        {
          id: "row-2",
          configuration: "Discos multicamada soldados a ponto",
          dimensions: "Ø 15 mm – 800 mm",
          meshRange: "Combinação 2 µm – 500 µm",
          edgeTreatment: "Solda de resistência perimetral",
          alloys: "AISI 304, 316, 316L",
        },
        {
          id: "row-3",
          configuration: "Discos com aro metálico selado",
          dimensions: "Ø 20 mm – 600 mm",
          meshRange: "Combinação 5 µm – 1.000 µm",
          edgeTreatment: "Aro dobrado de alumínio, cobre ou inox",
          alloys: "AISI 304, 316, 316L",
        },
        {
          id: "row-4",
          configuration: "Rolos cortados contínuos",
          dimensions: "Largura: 5 mm – 1.000 mm",
          meshRange: "10–500 mesh",
          edgeTreatment: "Corte rotativo / Estabilizado por ultrassom",
          alloys: "AISI 304, 316, 316L",
        },
        {
          id: "row-5",
          configuration: "Telas anulares e formatos especiais",
          dimensions: "Conforme desenho CAD",
          meshRange: "2 µm – 800 µm",
          edgeTreatment: "Aro cravado ou pontos de solda",
          alloys: "AISI 304, 316, 316L",
        },
      ],
      caption: "Certificados de ensaio de material (MTR) e inspeção PMI disponíveis sob consulta prévia no pedido. Ligas padrão: AISI 304, 316 e 316L.",
    },
    features: {
      heading: "Capacidades de fabricação e destaques de engenharia",
      intro: "Nossos processos de ferramentaria, corte e colocação de aros proporcionam uniformidade dimensional e alta confiabilidade mecânica.",
      items: [
        {
          title: "Tolerâncias geométricas rigorosas",
          text: "Ferramental em aço temperado que assegura concentricidade e tolerâncias de diâmetro de até ±0,05 mm sem folgas operacionais.",
        },
        {
          title: "Bordas regulares e livres de rebarbas",
          text: "Corte mecânico ou ultrassônico controlado para evitar soltura de fios em fluidos hidráulicos e polímeros sob pressão.",
        },
        {
          title: "Empilhamento multicamada estruturado",
          text: "Combinação de telas filtrantes finas com telas de sustentação robustas para aumentar a rigidez sob diferencial de pressão.",
        },
        {
          title: "Encapsulamento com aro perimetral",
          text: "Aros em alumínio dúctil, cobre ou aço inox que unem permanentemente as telas e exercem função de gaxeta vedante.",
        },
      ],
    },
    related: {
      heading: "Componentes de filtração relacionados",
      intro: "Conheça outros conjuntos e peças técnicas em tela metálica desenvolvidos para sistemas industriais de retenção e separação.",
      cards: [
        {
          title: "Visão geral de componentes",
          text: "Linha completa de tubos, discos, cestos e elementos de filtração em malha de arame de aço inoxidável.",
          to: "/FilterComponentsPage",
          badge: "Categoria mãe",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-616af2.png",
            alt: "Visão geral de componentes de filtração fabricados",
          },
          linkLabel: "Ver visão geral de componentes",
        },
        {
          title: "Tubos e cilindros filtrantes",
          text: "Cilindros de malha de arame soldados longitudinalmente e conformados para linhas de processo pressurizadas.",
          to: "/FilterTubesAndCylindersPage",
          badge: "Cilíndricos",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-616af2.png",
            alt: "Tubos e cilindros de tela de aço inoxidável",
          },
          linkLabel: "Ver tubos filtrantes",
        },
        {
          title: "Cartuchos filtrantes",
          text: "Cartuchos plissados e cilíndricos laváveis e reutilizáveis em aço inoxidável para líquidos e gases industriais.",
          to: "/FilterCartridgesPage",
          badge: "Cartuchos",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/Filter-Cartridges.jpg",
            alt: "Cartuchos filtrantes plissados de aço inoxidável",
          },
          linkLabel: "Ver cartuchos filtrantes",
        },
        {
          title: "Telas para extrusão (Screen Packs)",
          text: "Pacotes de telas multicamada com solda a ponto ou aros de alumínio para extrusão e moldagem de polímeros.",
          to: "/ScreenPacksPage",
          badge: "Extrusão",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-7f27c4.png",
            alt: "Telas multicamada para extrusoras de polímeros",
          },
          linkLabel: "Ver telas para extrusão",
        },
        {
          title: "Cestos e placas filtrantes",
          text: "Cestos reforçados e placas de suporte perfuradas para carcaças de filtro bolsa e filtros em linha.",
          to: "/FilterBasketsAndPlatesPage",
          badge: "Cestos",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-sta-d6091c.png",
            alt: "Cestos e placas filtrantes industriais de aço inoxidável",
          },
          linkLabel: "Ver cestos filtrantes",
        },
      ],
    },
    cta: {
      id: "slit-coils-stamped-discs-cta",
      eyebrow: "Fabricação sob medida de discos e rolos",
      heading: "Solicite cotação para discos estampados ou rolos cortados",
      lead: "Envie seus diâmetros de projeto, grau de retenção, camadas ou desenhos técnicos para avaliação.",
      secondaryLabel: "Central de componentes",
      secondaryTo: "/FilterComponentsPage",
      note: "Ferramental dedicado, solda por pontos e colocação de aros sob encomenda.",
    },
  },
};
